import * as React from 'react'
import clsx from 'clsx'
import { useForkRef, useEventCallback, useIsFocusVisible } from '../../hooks'
import {
  TouchRipple,
  TouchRippleProps,
  TouchRippleApiProps,
} from './TouchRipple'
import { SxProps, sx as sxFunc } from '../sx'
import styles from './index.module.scss'
type RippleActions = 'start' | 'stop'

export interface ButtonActionRefProp {
  focusVisible: () => void
}

export interface ButtonBaseClasses {
  /** Styles applied to the root element. */
  root?: string
  /** State class applied to the root element if `disabled={true}`. */
  disabled?: string
  /** State class applied to the root element if keyboard focused. */
  focusVisible?: string
}

export interface ButtonBaseProps
  extends Partial<React.HTMLAttributes<HTMLButtonElement>> {
  /**
   * A ref for imperative actions.
   * It currently only supports `focusVisible()` action.
   */
  action?: React.Ref<ButtonActionRefProp>
  /**
   * If `true`, the ripples are centered.
   * They won't start at the cursor interaction position.
   * @default false
   */
  centerRipple?: boolean
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: ButtonBaseClasses
  /**
   * @ignore
   */
  className?: string
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default button
   */
  Component?: React.ElementType
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean
  /**
   * If `true`, the ripple effect is disabled.
   *
   * ⚠️ Without a ripple there is no styling for :focus-visible by default. Be sure
   * to highlight the element by applying separate styles with the `focusVisible` classes.
   * @default false
   */
  disableRipple?: boolean
  /**
   * If `true`, the touch ripple effect is disabled.
   * @default false
   */
  disableTouchRipple?: boolean
  /**
   * If `true`, the base button will have a keyboard focus ripple.
   * @default false
   */
  focusRipple?: boolean
  /**
   * Callback fired when the component is focused with a keyboard.
   * We trigger a `onFocus` callback too.
   */
  onFocusVisible?: React.FocusEventHandler<HTMLButtonElement>
  /**
   * This prop can help identify which element has keyboard focus.
   * The class name will be applied when the element gains the focus through keyboard interaction.
   * It's a polyfill for the [CSS :focus-visible selector](https://drafts.csswg.org/selectors-4/#the-focus-visible-pseudo).
   * The rationale for using this feature [is explained here](https://github.com/WICG/focus-visible/blob/HEAD/explainer.md).
   * A [polyfill can be used](https://github.com/WICG/focus-visible) to apply a `focus-visible` class to other components
   * if needed.
   */
  focusVisibleClassName?: string
  /**
   * @ignore
   */
  href?: string
  /**
   * @ignore
   */
  to?: string
  /**
   * The component used to render a link when the `href` prop is provided.
   * @default 'a'
   */
  LinkComponent?: React.ElementType
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * @default 0
   */
  tabIndex?: number
  /**
   * Props applied to the `TouchRipple` element.
   */
  TouchRippleProps?: Omit<TouchRippleProps, 'center'>
  /**
   * A ref that points to the `TouchRipple` element.
   */
  touchRippleRef?: React.Ref<TouchRippleApiProps>
  /**
   * @ignore
   */
  type?: 'button' | 'reset' | 'submit'
}

/**
 * `ButtonBase` contains as few styles as possible.
 * It aims to be a simple building block for creating a button.
 * It contains a load of style reset and some focus/ripple logic.
 */
export const ButtonBase = React.forwardRef<HTMLButtonElement, ButtonBaseProps>(
  function ButtonBase(props, ref) {
    const {
      action,
      centerRipple = false,
      children,
      className,
      classes,
      Component = 'button',
      disabled = false,
      disableRipple = false,
      disableTouchRipple = false,
      focusRipple = false,
      focusVisibleClassName,
      LinkComponent = 'a',
      onBlur,
      onClick,
      onContextMenu,
      onDragLeave,
      onFocus,
      onFocusVisible,
      onKeyDown,
      onKeyUp,
      onMouseDown,
      onMouseLeave,
      onMouseUp,
      onTouchEnd,
      onTouchMove,
      onTouchStart,
      tabIndex = 0,
      TouchRippleProps,
      touchRippleRef,
      type,
      sx,
      ...other
    } = props

    const buttonRef = React.useRef<
      HTMLButtonElement | HTMLAnchorElement | null
    >(null)
    const [focusVisible, setFocusVisible] = React.useState(false)
    const [mountedState, setMountedState] = React.useState(false)
    const rippleRef = React.useRef<TouchRippleApiProps | null>(null)
    const handleRippleRef = useForkRef(rippleRef, touchRippleRef)
    const {
      isFocusVisibleRef,
      onFocus: handleFocusVisible,
      onBlur: handleBlurVisible,
      ref: focusVisibleRef,
    } = useIsFocusVisible()
    const handleRef = useForkRef(ref, focusVisibleRef, buttonRef)

    if (disabled && focusVisible) {
      setFocusVisible(false)
    }

    React.useImperativeHandle(
      action,
      () => ({
        focusVisible: () => {
          setFocusVisible(true)
          buttonRef?.current!.focus()
        },
      }),
      [setFocusVisible]
    )

    React.useEffect(() => {
      setMountedState(true)
    }, [])

    const enableTouchRipple = mountedState && !disableRipple && !disabled

    React.useEffect(() => {
      if (focusVisible && focusRipple && !disableRipple && mountedState) {
        rippleRef.current?.pulsate()
      }
    }, [disableRipple, focusRipple, focusVisible, mountedState])

    function useRippleHandler(
      rippleAction: RippleActions,
      eventCallback: any,
      skipRippleAction = disableTouchRipple
    ) {
      return useEventCallback(event => {
        if (eventCallback) {
          eventCallback(event)
        }

        const ignore = skipRippleAction
        if (!ignore && rippleRef.current) {
          // @ts-ignore
          rippleRef.current?.[rippleAction](event)
        }

        return true
      })
    }

    const handleMouseDown = useRippleHandler('start', onMouseDown)
    const handleContextMenu = useRippleHandler('stop', onContextMenu)
    const handleDragLeave = useRippleHandler('stop', onDragLeave)
    const handleMouseUp = useRippleHandler('stop', onMouseUp)
    const handleMouseLeave = useRippleHandler('stop', (event: any) => {
      if (focusVisible) {
        event.preventDefault()
      }
      if (onMouseLeave) {
        onMouseLeave(event)
      }
    })
    const handleTouchStart = useRippleHandler('start', onTouchStart)
    const handleTouchEnd = useRippleHandler('stop', onTouchEnd)
    const handleTouchMove = useRippleHandler('stop', onTouchMove)

    const handleBlur = useRippleHandler(
      'stop',
      (event: any) => {
        handleBlurVisible(event)
        if (isFocusVisibleRef.current === false) {
          setFocusVisible(false)
        }
        if (onBlur) {
          onBlur(event)
        }
      },
      false
    )

    const handleFocus = useEventCallback((event: any) => {
      // Fix for https://github.com/facebook/react/issues/7769
      if (!buttonRef.current) {
        buttonRef.current = event.currentTarget
      }

      handleFocusVisible(event)
      if (isFocusVisibleRef.current === true) {
        setFocusVisible(true)

        if (onFocusVisible) {
          onFocusVisible(event)
        }
      }

      if (onFocus) {
        onFocus(event)
      }
    })

    const isNonNativeButton = () => {
      const button = buttonRef.current
      return (
        Component &&
        Component !== 'button' &&
        !(button?.tagName === 'A' && (button as HTMLAnchorElement)?.href)
      )
    }

    /**
     * IE11 shim for https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/repeat
     */
    const keydownRef = React.useRef(false)
    const handleKeyDown = useEventCallback(
      (event: React.KeyboardEvent<HTMLButtonElement>) => {
        // Check if key is already down to avoid repeats being counted as multiple activations
        if (
          focusRipple &&
          !keydownRef.current &&
          focusVisible &&
          rippleRef.current &&
          event.key === ' '
        ) {
          keydownRef.current = true
          rippleRef.current.stop(event, () => {
            rippleRef.current?.start(event)
          })
        }

        if (
          event.target === event.currentTarget &&
          isNonNativeButton() &&
          event.key === ' '
        ) {
          event.preventDefault()
        }

        if (onKeyDown) {
          onKeyDown(event)
        }

        // Keyboard accessibility for non interactive elements
        if (
          event.target === event.currentTarget &&
          isNonNativeButton() &&
          event.key === 'Enter' &&
          !disabled
        ) {
          event.preventDefault()
          if (onClick) {
            onClick(event as any)
          }
        }
      }
    )

    const handleKeyUp = useEventCallback(
      (event: React.KeyboardEvent<HTMLButtonElement>) => {
        // calling preventDefault in keyUp on a <button> will not dispatch a click event if Space is pressed
        // https://codesandbox.io/s/button-keyup-preventdefault-dn7f0
        if (
          focusRipple &&
          event.key === ' ' &&
          rippleRef.current &&
          focusVisible &&
          !event.defaultPrevented
        ) {
          keydownRef.current = false
          rippleRef.current.stop(event, () => {
            rippleRef.current?.pulsate(event as any)
          })
        }
        if (onKeyUp) {
          onKeyUp(event)
        }

        // Keyboard accessibility for non interactive elements
        if (
          onClick &&
          event.target === event.currentTarget &&
          isNonNativeButton() &&
          event.key === ' ' &&
          !event.defaultPrevented
        ) {
          onClick(event as any)
        }
      }
    )

    let ComponentProp = Component

    if (ComponentProp === 'button' && (other.href || other.to)) {
      ComponentProp = LinkComponent
    }

    const buttonProps: any = {}
    if (ComponentProp === 'button') {
      buttonProps.type = type === undefined ? 'button' : type
      buttonProps.disabled = disabled
    } else {
      // @ts-ignore
      if (!other.href && !other.to) {
        buttonProps.role = 'button'
      }
      if (disabled) {
        buttonProps['aria-disabled'] = disabled
      }
    }

    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      React.useEffect(() => {
        if (enableTouchRipple && !rippleRef.current) {
          console.error(
            [
              'The `component` prop provided to ButtonBase is invalid.',
              'Please make sure the children prop is rendered in this custom component.',
            ].join('\n')
          )
        }
      }, [enableTouchRipple])
    }

    const _className = clsx(
      styles.root,
      classes?.root,
      disabled && [styles.disabled, classes?.disabled],
      focusVisible && ['Factor-focusVisible', classes?.focusVisible],
      focusVisible && focusVisibleClassName && `${focusVisibleClassName}`,
      sxFunc(sx),
      className
    )

    return (
      <ComponentProp
        className={_className}
        onBlur={handleBlur}
        onClick={onClick}
        onContextMenu={handleContextMenu}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onDragLeave={handleDragLeave}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onTouchStart={handleTouchStart}
        ref={handleRef}
        tabIndex={disabled ? -1 : tabIndex}
        type={type}
        {...buttonProps}
        {...other}
      >
        {children}
        {enableTouchRipple ? (
          /* TouchRipple is only needed client-side, x2 boost on the server. */
          <TouchRipple
            ref={handleRippleRef}
            {...TouchRippleProps}
            center={centerRipple}
          />
        ) : null}
      </ComponentProp>
    )
  }
)
